import { useState } from "react";
import { Badge } from "flowbite-react";
import { ImagePlaceholder } from "../../layouts/icons";
import EditMerchForm from "../../components/merchandise/manager/editMerchForm";
import { AssetGrid } from "./components";
import { DeleteMerchForm } from "../../components/merchandise/manager/deleteMerchForm";
import AllowedUsers from "../../routes/allowedUsers";
import Lightbox from "../lightbox";
import { PERMISSIONS } from "../../constants/permissions";

const MerchDetail = ({ item, handleApi }) => {
  const [imgIndex, setImgIndex] = useState(0);
  const [isEnlarged, setIsEnlarged] = useState(false);

  const assets = item.assets ?? [];

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
            {item.item_name}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">by {item.created_by}</p>
        </div>
        <div>
          <p className="text-[11px] text-gray-400 whitespace-nowrap">
            Updated{" "}
            <span className="text-gray-500 dark:text-gray-300">
              {item.updatedAt
                ? item.updatedAt.toDate().toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "-"}
            </span>
          </p>
        </div>
      </div>

      {/* ── Images ── */}
      <div className={`flex ${assets.length > 1 ? "gap-2" : ""}`}>
        {/* Thumbnails column */}
        <div className="flex flex-col gap-1.5 shrink-0">
          <AssetGrid
            assets={assets}
            imgIndex={imgIndex}
            setImgIndex={setImgIndex}
          />
        </div>

        {/* Main image */}
        {assets.length > 0 ? (
          <div
            className="relative flex-1 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800"
            style={{ aspectRatio: "4/3", maxHeight: "180px" }}
            onClick={() => setIsEnlarged(true)}
          >
            {assets.map((a, i) => (
              <img
                key={a.id}
                src={a.url}
                alt={a.fileName}
                className="w-full h-full object-contain absolute inset-0 transition-opacity duration-150"
                style={{
                  opacity: i === imgIndex ? 1 : 0,
                  pointerEvents: "none",
                }}
              />
            ))}
            {assets.length > 1 && (
              <>
                <button
                  onClick={() =>
                    setImgIndex((i) => (i - 1 + assets.length) % assets.length)
                  }
                  className="absolute left-1.5 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1 transition-colors"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setImgIndex((i) => (i + 1) % assets.length)}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-1 transition-colors"
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </>
            )}
          </div>
        ) : (
          <div
            className="flex-1 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center"
            style={{ maxHeight: "180px", aspectRatio: "4/3" }}
          >
            <ImagePlaceholder />
          </div>
        )}
        <Lightbox
          isOpen={isEnlarged}
          onClose={() => setIsEnlarged(false)}
          imgItem={item}
          imgIndex={imgIndex}
          imgGrid={
            <AssetGrid
              assets={item.assets}
              imgIndex={imgIndex}
              setImgIndex={setImgIndex}
              size={"w-[5rem] h-[5rem]"}
            />
          }
        />
      </div>

      {/* ── Info rows ── */}
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-1">
          Description
        </p>
        <p className="text-sm leading-6 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
          {item.item_purpose || (
            <span className="italic text-gray-400">
              No description provided.
            </span>
          )}
        </p>
      </div>
      {/* Details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-400">Stock</p>
          <div className="mt-2 flex items-end gap-2">
            <span className="text-xl font-bold text-gray-900 dark:text-white">
              {item.item_count}
            </span>
            <span className="text-sm text-gray-400 mb-1">items</span>
          </div>
          {item.restock_expected && Number(item.item_count) === 0 && (
            <div className="inline-flex">
              <Badge color="indigo" size="xs">
                Ordered · Available soon
              </Badge>
            </div>
          )}
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
          <p className="text-xs uppercase tracking-wide text-gray-400 flex items-center gap-2">
            <span>Storage</span>
            {item.storage_location && (
              <Badge color="indigo" size="xs">
                {{
                  syd: "Sydney",
                  melb: "Melbourne",
                }[item.storage_location] || item.storage_location}
              </Badge>
            )}
          </p>
          <p className="mt-2 text-medium font-medium text-gray-900 dark:text-white break-words">
            {item.item_location || (
              <span className="italic text-gray-400">Not set</span>
            )}
          </p>
        </div>
      </div>

      {/* ── Actions ── */}
      <div className="flex gap-2 mt-auto pt-2 border-t border-gray-100 dark:border-gray-700">
        <AllowedUsers permission={[PERMISSIONS.MERCH_EDITOR]}>
          <EditMerchForm item={item} handleApi={handleApi} />
          <DeleteMerchForm item={item} handleApi={handleApi} />
        </AllowedUsers>
      </div>
    </div>
  );
};

export default MerchDetail;
